import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get, every, isUndefined } from 'lodash-es';
/* Models */
import { VehicleEntities } from 'client/data/models/vehicle-v2';
/* Constants */
import { CHART_PROPS_REDESIGN, DEAL_LABEL, PRICE_COLOR } from 'site-modules/shared/constants/price-checker';
/* Utils */
import { getPriceString } from 'site-modules/shared/utils/price-utils';
import {
  getDealIndex,
  getPricingChartDescription,
  getTmvChartData,
  getVDPPriceData,
} from 'site-modules/shared/utils/price-checker';
import { randomInt } from 'client/utils/seed-randomizers';
import { numberWithCommas } from 'site-modules/shared/utils/string';
import { getPlural } from 'client/utils/plural';
/* Components */
import { PricingChart } from 'client/site-modules/shared/components/price-checker/price-checker-promo-chart/pricing-chart';
import {
  DEAL_TYPE_ICON,
  DEALS_SMALL_TEXT_COLORS_STYLE,
  FAIR_DEAL,
  GOOD_DEAL,
  GREAT_DEAL,
  HIGH_DEAL,
} from 'site-modules/shared/components/deal/deal-constants';
import { Gradient } from 'site-modules/shared/components/price-checker/price-checker-promo-chart/overlays/gradient/gradient';

const BARS_NUM_PER_DEAL_TYPE = 5;

export class PriceCheckerChartLeadForm extends Component {
  static propTypes = {
    tmvData: VehicleEntities.NewTmv,
    userDealerPrice: PropTypes.number,
    lowerPayment: PropTypes.number,
    upperPayment: PropTypes.number,
    isMobile: PropTypes.bool,
    dealerPrice: PropTypes.number,
    showLeasePayments: PropTypes.bool,
    currentTransactionCount: PropTypes.shape({
      tier1: PropTypes.number,
      tier2: PropTypes.number,
      tier3: PropTypes.number,
      tier4: PropTypes.number,
    }),
    forceLease: PropTypes.bool,
  };

  static defaultProps = {
    tmvData: null,
    userDealerPrice: null,
    lowerPayment: null,
    upperPayment: null,
    isMobile: false,
    dealerPrice: null,
    showLeasePayments: false,
    currentTransactionCount: null,
    forceLease: false,
  };

  constructor(props) {
    super(props);

    this.state = {
      tmvChartData: null,
    };
    this.filterRandomInt = randomInt();
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    const { tmvData, lowerPayment, upperPayment, userDealerPrice, dealerPrice } = nextProps;
    if (!tmvData || every(tmvData, price => price === 0)) {
      return {
        tmvChartData: null,
      };
    }

    let showPolyline;

    if (prevState && dealerPrice && numberWithCommas(dealerPrice) !== numberWithCommas(userDealerPrice)) {
      showPolyline = true;
    }

    const tmvChartData = getTmvChartData({
      tmvData,
      userDealerPrice,
    });
    const label = tmvChartData.dealerPrice.label;
    let index;

    if (userDealerPrice) {
      index = getDealIndex({
        tmvData,
        lowerPayment,
        upperPayment,
        userDealerPrice,
        label,
        barsPerDeal: BARS_NUM_PER_DEAL_TYPE,
      });
    }

    return {
      tmvChartData: {
        ...tmvChartData,
        dealerPrice: {
          ...tmvChartData.dealerPrice,
          index,
        },
        userDealerPrice,
        showPolyline,
      },
    };
  }

  render() {
    const {
      userDealerPrice,
      isMobile,
      dealerPrice,
      showLeasePayments,
      currentTransactionCount,
      forceLease,
      tmvData,
      lowerPayment,
      upperPayment,
    } = this.props;
    const { tmvChartData } = this.state;
    let customPriceLabels = [
      { index: CHART_PROPS_REDESIGN.COMMON.keyPointsIndex.lowEnd },
      { index: CHART_PROPS_REDESIGN.COMMON.keyPointsIndex.highEnd, end: true },
    ];
    if (tmvChartData) {
      customPriceLabels = [
        {
          label: tmvChartData.maxGreatPrice
            ? `${getPriceString(tmvChartData.maxGreatPrice)}${showLeasePayments ? '/mo.' : ''}`
            : '',
          index: CHART_PROPS_REDESIGN.COMMON.keyPointsIndex.lowEnd,
        },
        {
          label: tmvChartData.maxFairPrice
            ? `${getPriceString(tmvChartData.maxFairPrice)}${showLeasePayments ? '/mo.' : ''}`
            : '',
          index: CHART_PROPS_REDESIGN.COMMON.keyPointsIndex.highEnd,
          end: true,
        },
      ];
    }

    const dealerPriceDealType = get(getVDPPriceData({ ...tmvData, userDealerPrice: dealerPrice }), 'label');
    const dealerPriceIndex = getDealIndex({
      tmvData,
      lowerPayment,
      upperPayment,
      userDealerPrice: dealerPrice,
      label: dealerPriceDealType,
      barsPerDeal: BARS_NUM_PER_DEAL_TYPE,
    });

    let overlayPricingChartProps = { overlayConfig: CHART_PROPS_REDESIGN.COMMON.overlayConfig };
    const descriptionParams = [
      {
        tierTransactionCount: currentTransactionCount?.tier1,
        tierLabel: DEAL_LABEL.GREAT,
        maxPrice: tmvChartData?.maxGreatPrice,
      },
      {
        tierTransactionCount: currentTransactionCount?.tier2,
        tierLabel: DEAL_LABEL.GOOD,
        minPrice: tmvChartData?.maxGreatPrice,
        maxPrice: tmvChartData?.maxGoodPrice,
      },
      {
        tierTransactionCount: currentTransactionCount?.tier3,
        tierLabel: DEAL_LABEL.FAIR,
        minPrice: tmvChartData?.maxGoodPrice,
        maxPrice: tmvChartData?.maxFairPrice,
      },
      {
        tierTransactionCount: currentTransactionCount?.tier4,
        tierLabel: DEAL_LABEL.HIGH,
        minPrice: tmvChartData?.maxFairPrice,
      },
    ];
    const pricingChartDescription = getPricingChartDescription(descriptionParams);

    if (currentTransactionCount && tmvChartData) {
      const textProps = { textY: 20, textClassName: 'medium fw-medium' };
      const tier1 = get(currentTransactionCount, 'tier1');
      const tier2 = get(currentTransactionCount, 'tier2');
      const tier3 = get(currentTransactionCount, 'tier3');
      const tier4 = get(currentTransactionCount, 'tier4');
      const countCopy = 'sale';
      overlayPricingChartProps = {
        arrowLineTopSpace: 35,
        overlayConfig: {
          isOverlapArrow: false,
          component: Gradient,
          overlaysProps: [
            {
              ...CHART_PROPS_REDESIGN.COMMON.overlayConfig.overlaysProps[0],
              ...textProps,
              text: !isUndefined(tier1) ? `${tier1} ${getPlural(countCopy, tier1)}` : undefined,
              textColor: DEALS_SMALL_TEXT_COLORS_STYLE[GREAT_DEAL],
            },
            {
              ...CHART_PROPS_REDESIGN.COMMON.overlayConfig.overlaysProps[1],
              ...textProps,
              text: !isUndefined(tier2) ? `${tier2} ${getPlural(countCopy, tier2)}` : undefined,
              textColor: DEALS_SMALL_TEXT_COLORS_STYLE[GOOD_DEAL],
            },
            {
              ...CHART_PROPS_REDESIGN.COMMON.overlayConfig.overlaysProps[2],
              ...textProps,
              text: !isUndefined(tier3) ? `${tier3} ${getPlural(countCopy, tier3)}` : undefined,
              textColor: DEALS_SMALL_TEXT_COLORS_STYLE[FAIR_DEAL],
            },
            {
              ...CHART_PROPS_REDESIGN.COMMON.overlayConfig.overlaysProps[3],
              ...textProps,
              text: !isUndefined(tier4) ? `${tier4} ${getPlural(countCopy, tier4)}` : undefined,
              textColor: DEALS_SMALL_TEXT_COLORS_STYLE[HIGH_DEAL],
            },
          ],
        },
      };
    }

    const dealLabel = get(tmvChartData, 'dealerPrice.label');
    const dealerPriceEl = (
      <span className="small text-cool-gray-40" style={{ minHeight: '16px' }}>
        {!!userDealerPrice &&
          !!dealerPrice &&
          userDealerPrice === dealerPrice &&
          `${forceLease ? 'Based on dealer price' : 'Dealer Price'}`}
      </span>
    );
    const priceEl = (
      <div
        className={classnames('user-price fw-bold size-20', {
          'd-flex align-items-center justify-content-center': tmvChartData,
          invisible: !userDealerPrice || !tmvChartData,
        })}
      >
        <i style={{ color: PRICE_COLOR[dealLabel] }} className={DEAL_TYPE_ICON[dealLabel]} />
        <span className="text-lowercase ms-1">{`${dealLabel} Price`}</span>
      </div>
    );

    return (
      <div className="price-checker-chart-lead-form text-center">
        <div className="d-flex flex-column justify-content-end">
          <>
            {priceEl}
            <div
              className={classnames('text-cool-gray-10 size-30 fw-bold', {
                invisible: !userDealerPrice || !tmvChartData,
              })}
            >
              {getPriceString(userDealerPrice)}
              {showLeasePayments && '/mo.'}
            </div>
            {dealerPriceEl}
          </>
        </div>
        <PricingChart
          {...CHART_PROPS_REDESIGN.COMMON}
          {...overlayPricingChartProps}
          columnWidth={{ desktop: 27, mobile: 16 }}
          dataPoints={CHART_PROPS_REDESIGN.DATA_POINTS.WITH_DATA}
          selectedPriceIndex={get(tmvChartData, 'dealerPrice.index')}
          showPolyline={get(tmvChartData, 'showPolyline')}
          isMobile={isMobile}
          idPrefix={this.filterRandomInt.toString()}
          dealerPrice={dealerPrice}
          customChartHeight={165}
          dealerPriceIndex={dealerPriceIndex}
          labelVerticalShift={{ desktop: 25, mobile: 25 }}
          customPriceLabels={customPriceLabels}
          pricingChartDescription={pricingChartDescription}
          hideNoIndexArrow={!!userDealerPrice}
          yRate={2}
        />
      </div>
    );
  }
}
